import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../utils/api';
import { trackBannerClick } from '../utils/analyticsTracker';
import {
  normalizeSlideLinkFromApi,
  resolveSlideHref,
  isSlideLinkExternal,
  showDesktopCta,
  showMobileCta,
  isSlideImageClickable,
  shouldUseLegacyPromotionClick,
} from '../utils/bannerSlideLink';
import { useStorefrontSettings } from '../context/StorefrontSettingsContext';
import { pickMediaUrl } from '../utils/mediaUrl';
import './Banner.css';

const DEFAULT_BANNER_GROUPS = [
  {
    _id: 'default-1',
    isPromotion: true,
    displayOrder: 0,
    slides: [
      {
        _id: 'default-slide-1',
        title: 'Our Black November Continues',
        subtitle: 'Exclusive deals and massive discounts on all products',
        image:
          'https://images.unsplash.com/photo-1607082349566-187342175e2f?w=1400&h=400&fit=crop',
        imageMobile:
          'https://images.unsplash.com/photo-1607082349566-187342175e2f?w=1080&h=1080&fit=crop',
        link: '',
        linkType: 'none',
        linkTarget: '',
        cta: 'Shop Now',
        ctaDesktopVisible: true,
        ctaMobileVisible: true,
        ctaDesktopPosition: 'left',
      },
    ],
  },
];

const SLIDE_INTERVAL_MS = 10000;
const SWIPE_THRESHOLD_PX = 50;
const SWIPE_DOMINANCE_RATIO = 1.5;
const VALID_CTA_POSITIONS = new Set(['left', 'center', 'right']);
const BANNER_SWIPE_IGNORE_SELECTOR =
  '.banner-indicator, .banner-cta, .banner-cta-anchor, button';

function shouldIgnoreBannerSwipe(target) {
  return Boolean(target?.closest?.(BANNER_SWIPE_IGNORE_SELECTOR));
}

function normalizeCtaPosition(value) {
  return VALID_CTA_POSITIONS.has(value) ? value : 'left';
}

function normalizeSlide(slide, bannerId, index) {
  const linked = normalizeSlideLinkFromApi(slide);
  return {
    _id: slide._id || `${bannerId}-slide-${index}`,
    title: slide.title || '',
    subtitle: slide.subtitle || '',
    image: slide.image,
    imageMobile: slide.imageMobile || '',
    imageImagekit: slide.imageImagekit || '',
    imageMobileImagekit: slide.imageMobileImagekit || '',
    link: linked.link || '',
    linkType: linked.linkType,
    linkTarget: linked.linkTarget || '',
    cta: slide.cta || 'Shop Now',
    ctaDesktopVisible: slide.ctaDesktopVisible !== false,
    ctaMobileVisible: slide.ctaMobileVisible !== false,
    ctaDesktopPosition: normalizeCtaPosition(slide.ctaDesktopPosition),
    displayOrder: slide.displayOrder ?? index,
  };
}

function normalizeBannerGroups(bannersData = []) {
  return bannersData
    .map((banner) => {
      const bannerId = banner._id || banner.id;
      let slides = [];

      if (Array.isArray(banner.slides) && banner.slides.length > 0) {
        slides = [...banner.slides]
          .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
          .map((slide, index) => normalizeSlide(slide, bannerId, index));
      } else if (banner.image) {
        slides = [
          normalizeSlide(
            {
              title: banner.title,
              subtitle: banner.subtitle,
              image: banner.image,
              link: banner.link,
              cta: banner.cta,
            },
            bannerId,
            0
          ),
        ];
      }

      if (slides.length === 0) return null;

      return {
        _id: bannerId,
        name: banner.name || '',
        isPromotion: Boolean(banner.isPromotion),
        displayOrder: banner.displayOrder ?? 0,
        slides,
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.displayOrder - b.displayOrder);
}

function trackClick(activeGroup, slide, href) {
  trackBannerClick({
    bannerId: activeGroup._id,
    slideId: slide._id,
    title: slide.title,
    link: href || slide.link,
  });
}

function BannerNavigateControl({
  href,
  external,
  className,
  ariaLabel,
  onPromotionClick,
  usePromotionClick,
  onNavigate,
  children,
}) {
  if (usePromotionClick) {
    return (
      <button
        type="button"
        className={className}
        aria-label={ariaLabel}
        onClick={(e) => {
          e.preventDefault();
          if (onNavigate) onNavigate();
          if (onPromotionClick) onPromotionClick();
        }}
      >
        {children}
      </button>
    );
  }

  if (!href) return null;

  if (external) {
    return (
      <a
        href={href}
        className={className}
        aria-label={ariaLabel}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => {
          if (onNavigate) onNavigate();
        }}
      >
        {children}
      </a>
    );
  }

  return (
    <Link
      to={href}
      className={className}
      aria-label={ariaLabel}
      onClick={() => {
        if (onNavigate) onNavigate();
      }}
    >
      {children}
    </Link>
  );
}

function BannerSlideHitArea({ slide, activeGroup, deviceClass, onPromotionClick }) {
  const href = resolveSlideHref(slide);
  const usePromotionClick = shouldUseLegacyPromotionClick(slide, activeGroup.isPromotion);
  const clickable = usePromotionClick || Boolean(href);

  if (!clickable) return null;

  const external = isSlideLinkExternal(slide);
  const label = slide.title || slide.cta || 'Banner slide';

  return (
    <BannerNavigateControl
      href={href}
      external={external}
      className={`banner-slide-hit-area ${deviceClass}`}
      ariaLabel={label}
      usePromotionClick={usePromotionClick}
      onNavigate={() => trackClick(activeGroup, slide, href)}
      onPromotionClick={onPromotionClick}
    >
      {label}
    </BannerNavigateControl>
  );
}

function BannerCtaButton({ slide, activeGroup, onPromotionClick, className = 'banner-cta' }) {
  const href = resolveSlideHref(slide);
  const usePromotionClick = shouldUseLegacyPromotionClick(slide, activeGroup.isPromotion);
  const external = isSlideLinkExternal(slide);

  return (
    <BannerNavigateControl
      href={href}
      external={external}
      className={className}
      usePromotionClick={usePromotionClick}
      onNavigate={() => trackClick(activeGroup, slide, href)}
      onPromotionClick={onPromotionClick}
    >
      {slide.cta}
    </BannerNavigateControl>
  );
}

function BannerSlideContent({ slide, activeGroup, onPromotionClick }) {
  const hasTitle = String(slide.title || '').trim();
  const hasSubtitle = String(slide.subtitle || '').trim();
  const desktopCta = showDesktopCta(slide);
  const mobileCta = showMobileCta(slide);
  const desktopImageClick = isSlideImageClickable(slide, false);
  const mobileImageClick = isSlideImageClickable(slide, true);
  const href = resolveSlideHref(slide);
  const hasLegacyPromotion = shouldUseLegacyPromotionClick(slide, activeGroup.isPromotion);

  if (
    !hasTitle &&
    !hasSubtitle &&
    !desktopCta &&
    !mobileCta &&
    !desktopImageClick &&
    !mobileImageClick &&
    !hasLegacyPromotion
  ) {
    return null;
  }

  const position = normalizeCtaPosition(slide.ctaDesktopPosition);

  return (
    <>
      {desktopImageClick ? (
        <BannerSlideHitArea
          slide={slide}
          activeGroup={activeGroup}
          deviceClass="banner-slide-hit-area--desktop"
          onPromotionClick={onPromotionClick}
        />
      ) : null}
      {mobileImageClick ? (
        <BannerSlideHitArea
          slide={slide}
          activeGroup={activeGroup}
          deviceClass="banner-slide-hit-area--mobile"
          onPromotionClick={onPromotionClick}
        />
      ) : null}

      {(hasTitle || hasSubtitle) && (
        <>
          <div className="banner-content banner-content--desktop-copy">
            {hasTitle ? <h2 className="banner-title">{slide.title}</h2> : null}
            {hasSubtitle ? <p className="banner-subtitle">{slide.subtitle}</p> : null}
          </div>
          <div className="banner-content banner-content--mobile-copy">
            {hasTitle ? <h2 className="banner-title">{slide.title}</h2> : null}
            {hasSubtitle ? <p className="banner-subtitle">{slide.subtitle}</p> : null}
            {mobileCta ? (
              <BannerCtaButton
                slide={slide}
                activeGroup={activeGroup}
                onPromotionClick={onPromotionClick}
                className="banner-cta banner-cta--mobile"
              />
            ) : null}
          </div>
        </>
      )}

      {desktopCta ? (
        <div className={`banner-cta-anchor banner-cta-anchor--${position}`}>
          <BannerCtaButton
            slide={slide}
            activeGroup={activeGroup}
            onPromotionClick={onPromotionClick}
            className="banner-cta banner-cta--desktop"
          />
        </div>
      ) : null}

      {!hasTitle && !hasSubtitle && mobileCta ? (
        <div className="banner-content banner-content--mobile-copy banner-content--mobile-cta-only">
          <BannerCtaButton
            slide={slide}
            activeGroup={activeGroup}
            onPromotionClick={onPromotionClick}
            className="banner-cta banner-cta--mobile"
          />
        </div>
      ) : null}

      {!hasTitle && !hasSubtitle && !mobileCta && (href || hasLegacyPromotion) && !desktopImageClick && !mobileImageClick ? (
        <BannerSlideHitArea
          slide={slide}
          activeGroup={activeGroup}
          deviceClass="banner-slide-hit-area--desktop banner-slide-hit-area--mobile"
          onPromotionClick={onPromotionClick}
        />
      ) : null}
    </>
  );
}

const Banner = ({ onPromotionClick }) => {
  const { imagekitPrimaryEnabled } = useStorefrontSettings();
  const [bannerGroups, setBannerGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [position, setPosition] = useState({ bannerIndex: 0, slideIndex: 0 });
  const [autoplayPaused, setAutoplayPaused] = useState(false);
  const bannerGroupsRef = useRef(bannerGroups);
  const containerRef = useRef(null);
  const pauseReasonsRef = useRef({ hover: false, pointer: false });
  const gestureRef = useRef({
    active: false,
    pointerId: null,
    startX: 0,
    startY: 0,
    deltaX: 0,
    deltaY: 0,
  });
  const suppressClickRef = useRef(false);
  const positionRef = useRef(position);

  const syncAutoplayPaused = useCallback(() => {
    const { hover, pointer } = pauseReasonsRef.current;
    setAutoplayPaused(hover || pointer);
  }, []);

  const setHoverPaused = useCallback(
    (value) => {
      pauseReasonsRef.current.hover = value;
      syncAutoplayPaused();
    },
    [syncAutoplayPaused]
  );

  const setPointerPaused = useCallback(
    (value) => {
      pauseReasonsRef.current.pointer = value;
      syncAutoplayPaused();
    },
    [syncAutoplayPaused]
  );

  const resetGesture = useCallback(() => {
    gestureRef.current = {
      active: false,
      pointerId: null,
      startX: 0,
      startY: 0,
      deltaX: 0,
      deltaY: 0,
    };
  }, []);

  useEffect(() => {
    positionRef.current = position;
  }, [position]);

  useEffect(() => {
    bannerGroupsRef.current = bannerGroups;
  }, [bannerGroups]);

  useEffect(() => {
    fetchBanners();
  }, []);

  const fetchBanners = async () => {
    try {
      setLoading(true);
      const response = await api.get('/content/banners/active');
      let bannersData = [];

      if (Array.isArray(response)) {
        bannersData = response;
      } else if (response && typeof response === 'object') {
        if (Array.isArray(response.banners)) {
          bannersData = response.banners;
        } else if (Array.isArray(response.data)) {
          bannersData = response.data;
        }
      }

      const groups = normalizeBannerGroups(bannersData);
      setBannerGroups(groups.length > 0 ? groups : DEFAULT_BANNER_GROUPS);
      setPosition({ bannerIndex: 0, slideIndex: 0 });
    } catch (error) {
      console.error('Error fetching banners:', error);
      setBannerGroups(DEFAULT_BANNER_GROUPS);
      setPosition({ bannerIndex: 0, slideIndex: 0 });
    } finally {
      setLoading(false);
    }
  };

  const advanceCarousel = useCallback(() => {
    setPosition(({ bannerIndex, slideIndex }) => {
      const groups = bannerGroupsRef.current;
      if (groups.length === 0) {
        return { bannerIndex: 0, slideIndex: 0 };
      }

      const currentSlides = groups[bannerIndex]?.slides || [];
      if (slideIndex + 1 < currentSlides.length) {
        return { bannerIndex, slideIndex: slideIndex + 1 };
      }

      return {
        bannerIndex: (bannerIndex + 1) % groups.length,
        slideIndex: 0,
      };
    });
  }, []);

  const goToSlide = useCallback((index) => {
    setPosition((prev) => ({ ...prev, slideIndex: index }));
  }, []);

  const goToRelativeSlide = useCallback((direction) => {
    setPosition(({ bannerIndex, slideIndex }) => {
      const groups = bannerGroupsRef.current;
      const slides = groups[bannerIndex]?.slides || [];
      if (slides.length <= 1) {
        return { bannerIndex, slideIndex };
      }
      const nextIndex = (slideIndex + direction + slides.length) % slides.length;
      return { bannerIndex, slideIndex: nextIndex };
    });
  }, []);

  const isSwipeGesture = useCallback((deltaX, deltaY) => {
    return (
      Math.abs(deltaX) >= SWIPE_THRESHOLD_PX &&
      Math.abs(deltaX) > Math.abs(deltaY) * SWIPE_DOMINANCE_RATIO
    );
  }, []);

  const handlePointerDown = useCallback(
    (event) => {
      setPointerPaused(true);

      if (shouldIgnoreBannerSwipe(event.target)) {
        return;
      }

      const groups = bannerGroupsRef.current;
      const slideCount = groups[positionRef.current.bannerIndex]?.slides?.length || 0;
      if (slideCount <= 1) {
        return;
      }

      gestureRef.current = {
        active: true,
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        deltaX: 0,
        deltaY: 0,
      };
    },
    [setPointerPaused]
  );

  const handlePointerMove = useCallback(
    (event) => {
      const gesture = gestureRef.current;
      if (!gesture.active || gesture.pointerId !== event.pointerId) {
        return;
      }

      const deltaX = event.clientX - gesture.startX;
      const deltaY = event.clientY - gesture.startY;
      gesture.deltaX = deltaX;
      gesture.deltaY = deltaY;

      if (isSwipeGesture(deltaX, deltaY) && event.cancelable) {
        event.preventDefault();
      }
    },
    [isSwipeGesture]
  );

  const finishPointerGesture = useCallback(
    (event) => {
      const gesture = gestureRef.current;
      if (!gesture.active || gesture.pointerId !== event.pointerId) {
        setPointerPaused(false);
        return;
      }

      const { deltaX, deltaY } = gesture;
      const didSwipe = isSwipeGesture(deltaX, deltaY);

      if (didSwipe) {
        if (deltaX < 0) {
          goToRelativeSlide(1);
        } else {
          goToRelativeSlide(-1);
        }
        if (event.target?.closest?.('.banner-slide-hit-area')) {
          suppressClickRef.current = true;
          window.setTimeout(() => {
            suppressClickRef.current = false;
          }, 0);
        }
        if (event.cancelable) {
          event.preventDefault();
        }
      }

      resetGesture();
      setPointerPaused(false);
    },
    [goToRelativeSlide, isSwipeGesture, resetGesture, setPointerPaused]
  );

  const handleClickCapture = useCallback((event) => {
    if (!suppressClickRef.current) {
      return;
    }
    if (!event.target?.closest?.('.banner-slide-hit-area')) {
      return;
    }
    suppressClickRef.current = false;
    event.preventDefault();
    event.stopPropagation();
  }, []);

  useEffect(() => {
    if (bannerGroups.length === 0 || autoplayPaused) {
      return undefined;
    }

    const interval = setInterval(advanceCarousel, SLIDE_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [bannerGroups.length, advanceCarousel, autoplayPaused]);

  if (loading) {
    return (
      <section className="banner-section">
        <div className="banner-container banner-container--skeleton">
          <div className="banner-skeleton" />
        </div>
      </section>
    );
  }

  if (bannerGroups.length === 0) {
    return null;
  }

  const { bannerIndex, slideIndex } = position;
  const activeGroup = bannerGroups[bannerIndex] || bannerGroups[0];
  const activeSlides = activeGroup?.slides || [];

  return (
    <section className="banner-section">
      <div
        ref={containerRef}
        className="banner-container"
        key={activeGroup._id}
        onMouseEnter={() => setHoverPaused(true)}
        onMouseLeave={() => setHoverPaused(false)}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={finishPointerGesture}
        onPointerCancel={finishPointerGesture}
        onClickCapture={handleClickCapture}
      >
        {activeSlides.map((slide, index) => (
          <div
            key={slide._id}
            className={`banner-slide ${index === slideIndex ? 'active' : ''}`}
          >
            <picture>
              <source
                media="(max-width: 768px)"
                srcSet={
                  pickMediaUrl({
                    primary: slide.imageMobile || slide.image,
                    imagekit: slide.imageMobileImagekit || slide.imageImagekit,
                    preferImageKit: imagekitPrimaryEnabled,
                  }) || slide.imageMobile || slide.image
                }
              />
              <img
                src={
                  pickMediaUrl({
                    primary: slide.image,
                    imagekit: slide.imageImagekit,
                    preferImageKit: imagekitPrimaryEnabled,
                  }) || slide.image
                }
                alt=""
                className="banner-slide-image"
                loading={bannerIndex === 0 && index === 0 ? 'eager' : 'lazy'}
                decoding="async"
                fetchPriority={bannerIndex === 0 && index === 0 ? 'high' : 'auto'}
                draggable={false}
                onError={(e) => {
                  const primary = slide.image || '';
                  const ik = slide.imageImagekit || '';
                  const src = e.currentTarget.src || '';
                  if (imagekitPrimaryEnabled && primary && !src.includes(primary.split('?')[0])) {
                    e.currentTarget.src = primary;
                    return;
                  }
                  if (!imagekitPrimaryEnabled && ik && !/ik\.imagekit\.io/i.test(src)) {
                    e.currentTarget.src = ik;
                  }
                }}
              />
            </picture>
            <BannerSlideContent
              slide={slide}
              activeGroup={activeGroup}
              onPromotionClick={onPromotionClick}
            />
          </div>
        ))}
        {activeSlides.length > 1 && (
          <div className="banner-indicators">
            {activeSlides.map((slide, index) => (
              <button
                key={slide._id}
                type="button"
                className={`banner-indicator ${index === slideIndex ? 'active' : ''}`}
                onClick={() => goToSlide(index)}
                aria-label={`Go to slide ${index + 1} of ${activeSlides.length}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default Banner;
